import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seo from './pages.json';

interface Page { path: string; title: string; description: string }

export const canonicalFor = (path: string) =>
  seo.siteUrl + (path === '/' ? '/' : `${path}/`);

function upsert(selector: string, create: () => HTMLElement, attr: string, value: string) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function meta(key: 'name' | 'property', name: string, content: string) {
  upsert(
    `meta[${key}="${name}"]`,
    () => { const m = document.createElement('meta'); m.setAttribute(key, name); return m; },
    'content',
    content,
  );
}

// Keeps <title>, description, canonical, robots and social tags in step with the
// current route. Pages not listed in pages.json (dashboard, login, ...) are noindex.
export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const page = (seo.pages as Page[]).find((p) => p.path === path);

    const title = page?.title ?? seo.defaultTitle;
    const description = page?.description ?? seo.defaultDescription;
    const image = seo.siteUrl + seo.defaultImage;

    document.title = title;
    meta('name', 'description', description);
    meta('name', 'robots', page ? 'index,follow' : 'noindex,nofollow');

    meta('property', 'og:type', 'website');
    meta('property', 'og:site_name', seo.siteName);
    meta('property', 'og:title', title);
    meta('property', 'og:description', description);
    meta('property', 'og:image', image);
    meta('name', 'twitter:card', 'summary_large_image');
    meta('name', 'twitter:title', title);
    meta('name', 'twitter:description', description);
    meta('name', 'twitter:image', image);

    if (page) {
      const url = canonicalFor(page.path);
      upsert('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, 'href', url);
      meta('property', 'og:url', url);
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
  }, [pathname]);

  return null;
}
